import React, { useMemo, useRef, useEffect, useState } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  Position,
  ConnectionMode,
  MarkerType,
  Handle,
  useReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import ELK, { ElkNode } from 'elkjs/lib/elk.bundled.js';
import { getInitials } from '@airclou/utils';

export interface OrgPerson extends Record<string, unknown> {
  id: string;
  name: string;
  title: string;
  department: string;
  email: string;
  avatar?: string;
  reportsTo?: string; // ID of the person they report to
}

export interface OrganizationChartProps {
  people: OrgPerson[];
  className?: string;
}

// Custom node component for organization chart
const OrgNode = ({ data }: { data: OrgPerson }) => {
  const getDepartmentColor = (department: string) => {
    const colors = {
      'Engineering': 'bg-blue-600',
      'Technology': 'bg-blue-600',
      'Product': 'bg-purple-600',
      'Sales': 'bg-green-600',
      'Design': 'bg-pink-600',
      'Marketing': 'bg-orange-600',
      'Human Resources': 'bg-teal-600',
      'Finance': 'bg-indigo-600',
      'Executive': 'bg-gray-800',
      'Operations': 'bg-red-600',
    };
    return colors[department as keyof typeof colors] || 'bg-gray-600';
  };

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm min-w-[200px] relative">
      <Handle type="target" position={Position.Top} className="!bg-gray-400 !border-gray-300" />
      <Handle type="source" position={Position.Bottom} className="!bg-gray-400 !border-gray-300" />
      
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 ${getDepartmentColor(data.department)} rounded-full flex items-center justify-center flex-shrink-0`}>
          <span className="text-white font-bold text-sm">{getInitials(data.name)}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-foreground text-sm truncate">{data.name}</h3>
          <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{data.title}</p>
        </div>
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-500 truncate">{data.department}</div>
      <div className="text-xs text-gray-400 dark:text-gray-600 truncate mt-1">{data.email}</div>
    </div>
  );
};

const nodeTypes = {
  orgNode: OrgNode,
};

const elk = new ELK();

// ELK layout options
const elkOptions = {
  'elk.algorithm': 'layered',
  'elk.layered.spacing.nodeNodeBetweenLayers': '100',
  'elk.spacing.nodeNode': '80',
  'elk.direction': 'DOWN',
  'elk.layered.nodePlacement.strategy': 'SIMPLE',
};

const getLayoutedElements = async (nodes: Node[], edges: Edge[]) => {
  const graph: ElkNode = {
    id: 'root',
    layoutOptions: elkOptions,
    children: nodes.map((node) => ({
      id: node.id,
      // Specify node dimensions for ELK
      width: 220,
      height: 120,
    })),
    edges: edges.map(edge => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
    })),
  };

  try {
    const layoutedGraph = await elk.layout(graph);

    const layoutedNodes = nodes.map((node) => {
      const layoutedNode = layoutedGraph.children?.find((lgNode) => lgNode.id === node.id);
      if (layoutedNode) {
        node.position = {
          x: layoutedNode.x ?? 0,
          y: layoutedNode.y ?? 0,
        };
      }
      return node;
    });

    // Calculate graph bounds for viewport calculation
    const layoutedChildren = layoutedGraph.children || [];
    let graphBounds = null;
    
    if (layoutedChildren.length > 0) {
      const minX = Math.min(...layoutedChildren.map(child => child.x || 0));
      const maxX = Math.max(...layoutedChildren.map(child => (child.x || 0) + (child.width || 0)));
      const minY = Math.min(...layoutedChildren.map(child => child.y || 0));
      const maxY = Math.max(...layoutedChildren.map(child => (child.y || 0) + (child.height || 0)));
      
      graphBounds = {
        width: maxX - minX,
        height: maxY - minY,
        centerX: (minX + maxX) / 2,
        centerY: (minY + maxY) / 2,
      };
    }

    return { nodes: layoutedNodes, edges, graphBounds };
  } catch (error) {
    console.error('ELK layout error:', error);
    return { nodes, edges, graphBounds: null };
  }
};

function OrganizationChartComponent({ people, className = '' }: OrganizationChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([] as Node[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);
  const [viewport, setViewport] = useState({ x: 0, y: 0, zoom: 1 });
  const [isReady, setIsReady] = useState(false);
  const { fitView: _fitView } = useReactFlow();

  // Dynamic dark mode detection
  const [isDarkMode, setIsDarkMode] = React.useState(() => 
    document.documentElement.classList.contains('dark')
  );

  // Watch for theme changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          setIsDarkMode(document.documentElement.classList.contains('dark'));
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const { initialNodes, initialEdges } = useMemo(() => {
    const nodes: Node[] = people.map((person) => ({
      id: person.id,
      type: 'orgNode',
      data: person,
      position: { x: 0, y: 0 }, // Will be set by ELK
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    }));

    const edges: Edge[] = people
      .filter((person) => person.reportsTo)
      .map((person) => ({
        id: `edge-${person.reportsTo}-${person.id}`,
        source: person.reportsTo!,
        target: person.id,
        type: 'smoothstep',
        style: { 
          stroke: isDarkMode ? '#9ca3af' : '#6b7280', 
          strokeWidth: 2,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isDarkMode ? '#9ca3af' : '#6b7280',
        },
      }));

    return { initialNodes: nodes, initialEdges: edges };
  }, [people, isDarkMode]);

  // Calculate viewport based on container dimensions
  const calculateViewport = (containerWidth: number, containerHeight: number, graphBounds: any) => {
    if (!graphBounds) return { x: 0, y: 0, zoom: 1 };
    
    const padding = 0.1;
    const availableWidth = containerWidth * (1 - padding * 2);
    const availableHeight = containerHeight * (1 - padding * 2);
    
    const scaleX = availableWidth / graphBounds.width;
    const scaleY = availableHeight / graphBounds.height;
    const zoom = Math.min(scaleX, scaleY, 1); // Don't zoom in beyond 1x
    
    // Calculate position to center the graph
    const x = containerWidth / 2 - graphBounds.centerX * zoom;
    const y = containerHeight / 2 - graphBounds.centerY * zoom;
    
    return { x, y, zoom };
  };

  // Wait for container to be properly sized before calculating viewport
  useEffect(() => {
    const waitForContainer = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // Ensure container has proper dimensions
        if (rect.width > 0 && rect.height > 0) {
          // Container is ready, now calculate layout and viewport
          setIsReady(false);
          
          getLayoutedElements(initialNodes, initialEdges).then(({ nodes: layoutedNodes, edges: layoutedEdges, graphBounds }) => {
            setNodes(layoutedNodes);
            setEdges(layoutedEdges);
            
            // Calculate viewport with confirmed container dimensions
            if (graphBounds) {
              const newViewport = calculateViewport(rect.width, rect.height, graphBounds);
              setViewport(newViewport);
            }
            
            setIsReady(true);
          });
        } else {
          // Container not ready yet, keep checking
          requestAnimationFrame(waitForContainer);
        }
      } else {
        // Container ref not available yet, keep checking
        requestAnimationFrame(waitForContainer);
      }
    };

    // Reset and wait for container on data change
    setIsReady(false);
    waitForContainer();
  }, [initialNodes, initialEdges, setNodes, setEdges]);
  
  // This effect uses a ResizeObserver to recalculate viewport on container resize.
  useEffect(() => {
    if (!containerRef.current || !isReady) return;

    const resizeObserver = new ResizeObserver(() => {
      if (!containerRef.current) return;
      
      // Recalculate viewport based on new container dimensions
      getLayoutedElements(initialNodes, initialEdges).then(({ graphBounds }) => {
        if (graphBounds) {
          const rect = containerRef.current!.getBoundingClientRect();
          const newViewport = calculateViewport(rect.width, rect.height, graphBounds);
          setViewport(newViewport);
        }
      });
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [initialNodes, initialEdges, isReady]);

  return (
    <div ref={containerRef} className={`w-full h-[600px] border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 ${className}`}>
      {isReady ? (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          connectionMode={ConnectionMode.Loose}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          minZoom={0.1}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
          defaultViewport={viewport}
          style={{
            backgroundColor: 'transparent',
            borderRadius: '8px'
          }}
        >
        </ReactFlow>
      ) : (
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-500 dark:text-gray-400">Loading...</div>
        </div>
      )}
    </div>
  );
}

export function OrganizationChart(props: OrganizationChartProps) {
  return (
    <ReactFlowProvider>
      <OrganizationChartComponent {...props} />
    </ReactFlowProvider>
  );
} 